import { Check, Loader2, Pencil, Save } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"

import {
  buildMentionHandlesAttributes,
  saveMentionHandleGroup,
} from "@/api/mention-handles"
import PlatformIcon from "@/components/icons/platform-icon"
import MentionHandleFields from "@/components/mention-handle-fields"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  handleValidationState,
  mentionLabelsInBody,
} from "@/lib/mention-handle-validation"
import type { MentionHandleGroup, Platform } from "@/types"

interface MentionEditorProps {
  body: string
  mentionHandleGroups: MentionHandleGroup[]
  mentionPlatforms: Platform[]
  mastodonServerDomain?: string | null
  onMentionGroupSaved: (group: MentionHandleGroup) => void
}

type MentionHandleFormState = Record<string, string>

function defaultHandleForPlatform(
  label: string,
  platform: string,
  mastodonServerDomain?: string | null,
): string {
  if (platform === "bluesky") return `@${label}.bsky.social`
  if (platform === "mastodon" && mastodonServerDomain) {
    const domain = mastodonServerDomain
      .replace(/^https?:\/\//, "")
      .replace(/\/$/, "")
    return `@${label}@${domain}`
  }
  return `@${label}`
}

function buildHandleState(
  label: string,
  platforms: Platform[],
  mastodonServerDomain?: string | null,
  existingGroup?: MentionHandleGroup,
): MentionHandleFormState {
  const state: MentionHandleFormState = {}
  for (const platform of platforms) {
    const existing = existingGroup?.mention_handles.find(
      (h) => h.platform.name === platform.name,
    )
    if (existing) {
      state[platform.name] = existing.handle
    } else if (existingGroup) {
      // Existing group with no handle for this platform: leave it blank rather
      // than pre-populating a default the user never entered.
      state[platform.name] = ""
    } else {
      // New group: seed a sensible default handle per platform.
      state[platform.name] = defaultHandleForPlatform(
        label,
        platform.name,
        mastodonServerDomain,
      )
    }
  }
  return state
}

function MentionRow({
  label,
  mentionPlatforms,
  mastodonServerDomain,
  existingGroup,
  onSaved,
}: {
  label: string
  mentionPlatforms: Platform[]
  mastodonServerDomain?: string | null
  existingGroup?: MentionHandleGroup
  onSaved: (group: MentionHandleGroup) => void
}) {
  const [editing, setEditing] = useState(!existingGroup)
  const [handles, setHandles] = useState<MentionHandleFormState>(() =>
    buildHandleState(
      label,
      mentionPlatforms,
      mastodonServerDomain,
      existingGroup,
    ),
  )
  const [fallbackText, setFallbackText] = useState(
    existingGroup?.fallback_text ?? "",
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const savedTimerRef = useRef<ReturnType<typeof setTimeout>>(null)

  // Sync form state when existingGroup changes (e.g. saved from another take).
  // Deliberately resets local edit state from the canonical prop, so the
  // setState-in-effect is intentional here.
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    setHandles(
      buildHandleState(
        label,
        mentionPlatforms,
        mastodonServerDomain,
        existingGroup,
      ),
    )
    setFallbackText(existingGroup?.fallback_text ?? "")
    if (existingGroup) setEditing(false)
  }, [existingGroup, label, mentionPlatforms, mastodonServerDomain])
  /* eslint-enable react-hooks/set-state-in-effect */

  const hasInvalidHandles = mentionPlatforms.some((platform) => {
    const validation = handleValidationState(
      platform,
      handles[platform.name] ?? "",
    )
    return validation?.valid === false
  })

  const isUnsaved = !existingGroup

  const isDirty = useCallback(() => {
    if (!existingGroup) return false
    if (fallbackText !== (existingGroup.fallback_text ?? "")) return true
    for (const platform of mentionPlatforms) {
      const currentValue = handles[platform.name] ?? ""
      const savedValue =
        existingGroup.mention_handles.find(
          (h) => h.platform.name === platform.name,
        )?.handle ?? ""
      if (currentValue !== savedValue) return true
    }
    return false
  }, [handles, fallbackText, existingGroup, mentionPlatforms])

  function handleCancel() {
    setHandles(
      buildHandleState(
        label,
        mentionPlatforms,
        mastodonServerDomain,
        existingGroup,
      ),
    )
    setFallbackText(existingGroup?.fallback_text ?? "")
    setError(null)
    setEditing(false)
  }

  async function handleSave() {
    setSaving(true)
    setError(null)

    const result = await saveMentionHandleGroup(
      {
        label,
        fallback_text: fallbackText,
        mention_handles_attributes: buildMentionHandlesAttributes(
          mentionPlatforms,
          handles,
          existingGroup,
        ),
      },
      existingGroup?.id,
    )

    if (!result.ok) {
      setError(result.errors.join(", ") || "Failed to save mention")
      setSaving(false)
      return
    }

    onSaved(result.data)
    setSaved(true)
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current)
    savedTimerRef.current = setTimeout(() => setSaved(false), 2000)
    setSaving(false)
  }

  if (!editing) {
    return (
      <div className="flex items-center justify-between rounded-lg border p-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <p className="font-mono text-sm font-medium">@{label}</p>
          <div className="text-muted-foreground flex flex-wrap gap-x-3 gap-y-1 text-xs">
            {mentionPlatforms.map((platform) => {
              const mentionHandle = existingGroup?.mention_handles.find(
                (h) => h.platform.name === platform.name,
              )
              if (!mentionHandle) return null
              return (
                <span key={platform.name} className="flex items-center gap-1">
                  <PlatformIcon platform={platform.name} className="size-3" />
                  {mentionHandle.profile_url ? (
                    <a
                      href={mentionHandle.profile_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-foreground decoration-muted-foreground/50 hover:decoration-foreground font-mono underline underline-offset-2"
                    >
                      {mentionHandle.handle}
                    </a>
                  ) : (
                    <span className="font-mono">{mentionHandle.handle}</span>
                  )}
                </span>
              )
            })}
          </div>
          {existingGroup?.fallback_text && (
            <span className="text-muted-foreground text-xs">
              fallback: {existingGroup.fallback_text}
            </span>
          )}
          {saved && (
            <span className="inline-flex items-center gap-1 text-xs text-green-700">
              <Check className="size-3.5" />
              Saved
            </span>
          )}
        </div>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="size-7"
          onClick={() => setEditing(true)}
        >
          <Pencil className="size-3.5" />
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3 rounded-lg border p-3">
      <p className="font-mono text-sm font-medium">@{label}</p>

      <MentionHandleFields
        mentionPlatforms={mentionPlatforms}
        handles={handles}
        onHandleChange={(platformName, value) =>
          setHandles((prev) => ({ ...prev, [platformName]: value }))
        }
        placeholderLabel={`@${label}`}
      />

      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`mention-fallback-${label}`} className="text-xs">
          Fallback text
        </Label>
        <Input
          id={`mention-fallback-${label}`}
          value={fallbackText}
          onChange={(e) => setFallbackText(e.target.value)}
          placeholder={`@${label}`}
          className="h-8 text-sm"
        />
        <p className="text-muted-foreground text-xs">
          Swapped in on platforms with no handle for this mention.
        </p>
      </div>

      {error && <p className="text-destructive text-xs">{error}</p>}

      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={saving || hasInvalidHandles || (!isUnsaved && !isDirty())}
          onClick={() => void handleSave()}
        >
          {saving ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <Save className="size-3.5" />
          )}
          {existingGroup ? "Update" : "Save"} handles
        </Button>
        {existingGroup && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleCancel}
          >
            Cancel
          </Button>
        )}
      </div>
    </div>
  )
}

export default function MentionEditor({
  body,
  mentionHandleGroups,
  mentionPlatforms,
  mastodonServerDomain,
  onMentionGroupSaved,
}: MentionEditorProps) {
  const labels = mentionLabelsInBody(body)
  const groupsByLabel = Object.fromEntries(
    mentionHandleGroups.map((group) => [group.label, group]),
  )

  if (labels.length === 0) return null

  return (
    <div className="flex flex-col gap-3">
      <p className="text-muted-foreground text-sm">
        Configure how each @mention appears on each platform:
      </p>
      {labels.map((label) => (
        <MentionRow
          key={label}
          label={label}
          mentionPlatforms={mentionPlatforms}
          mastodonServerDomain={mastodonServerDomain}
          existingGroup={groupsByLabel[label]}
          onSaved={onMentionGroupSaved}
        />
      ))}
    </div>
  )
}
